import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges, TrackByFunction } from '@angular/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { ModalService } from '@app/core/services/modal.service';
import { Subject, takeUntil, switchMap, from, catchError, EMPTY, debounce, timer, filter } from 'rxjs';
import { ChecklistDataGet, ChecklistDataParams, ChecklistDataService, ChecklistDataSetting, ChecklistItem, ComputeValidationData, ComputeValidationDataResult, ComputeValidationResponse, SaveChecklistDataParams } from './checklist-data.service';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { KeyValue } from '@angular/common';
import { StateDialogContext, TT_STATE_DIALOG, TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';

// @GridModalComponent('checklistdata')
@Component({
    selector: 'tt-checklist-data',
    templateUrl: './checklist-data.component.html',
    styleUrls: ['./checklist-data.component.css'],
})
export class ChecklistDataComponent extends ComponentBaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: ChecklistDataParams;

    public settings?: ChecklistDataSetting;

    public get?: ChecklistDataGet;

    public saving = false;

    public deleting = false;

    public compute$ = new Subject<ChecklistItem>();

    public destroy$ = new Subject<void>();

    // selectedTab: string = '1';

    public pages = new Map<ChecklistItem, { items: ChecklistItem[]; complete: boolean; invalid: boolean }>();

    @Output()
    public submit = new EventEmitter<ErrorMessageResponse | undefined>();

    @Output()
    public settingsLoaded = new EventEmitter<ChecklistDataSetting>();

    constructor(
        private chechklistData: ChecklistDataService,
        private modal: ModalService,
        // @Optional() @Inject(TT_STATE_DIALOG) stateDialog: StateDialogContext,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator: TTViewNavigator,
    ) {
        super();
        this.compute$
            .pipe(
                takeUntil(this.destroy$),
                filter((item) => item.cv === '1'),
                debounce((item) => timer(['DT', 'CL', 'CB', 'CS'].includes(item.elementtype) ? 50 : 500)),
                switchMap((item) => from(this.computeValidationForItem(item)).pipe(catchError(() => EMPTY))),
            )
            .subscribe({ next: this.onComputeValidationNext });
        // this.initFromDialog(stateDialog);
    }

    public trackChecklistItemBy: TrackByFunction<ChecklistItem> = (_: number, item: ChecklistItem) => {
        return item.checklistitem_keyno;
    };

    public trackPagesBy: TrackByFunction<KeyValue<ChecklistItem, PageStep>> = (_: number, item: KeyValue<ChecklistItem, PageStep>) => {
        return item.key.checklistitem_keyno;
    };

    private onComputeValidationNext = (response: ComputeValidationResponse | undefined) => {
        if (!this.get || !response) return;

        const { errorcode, errormessage, outputresult } = response;

        if ('' + errorcode !== '0') {
            this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
        }

        if (!outputresult) return;

        for (let data of outputresult) {
            this.setComputeValidationResult(data);
        }
    };

    public async save(params?: Partial<Pick<SaveChecklistDataParams, 'approve'>>) {
        try {
            this.saving = true;

            const response = await this.saveChecklistData(params);

            this.submit.emit(response);
            this.navigator.back(undefined, response);
        } catch (error) {
            throw error;
        } finally {
            this.saving = false;
        }
    }

    public async deleteChecklistData() {
        if (!this.get) return;

        try {
            this.deleting = true;

            const confirmed = await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_title', translateTitle: true, message: 'confirm_message', translateMessage: true });

            if (!!confirmed) {
                const response = await this.chechklistData.deleteChecklistData({ checklistdata_keyno: this.get.checklistdata_keyno });

                if (!!response && typeof response === 'object' && 'iserror' in response && response.iserror === false) {
                    this.submit.emit();
                    this.navigator.back();
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    public saveChecklistData = async (params?: Partial<Pick<SaveChecklistDataParams, 'approve'>>) => {
        if (!this.get) return;

        const { errorcode, errormessage } = await this.chechklistData.saveChecklistData({
            checklistdata_keyno: this.get.checklistdata_keyno,
            checklist_keyno: this.get.checklist_keyno,
            checklist_name: this.get.checklist_name,
            approve: params?.approve ?? '0',
            checklistitems: this.get.checklistitems.map((item) => ({ i: item.checklistitemdata_keyno, v: item.itemvalue })),
        });

        if ('' + errorcode !== '0') {
            this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            throw Error(errormessage);
        } else {
            return { errorcode, errormessage };
        }
    };

    public onChecklistItemChange(item: ChecklistItem) {
        if (!this.get) return;

        this.setDependencyVisibilityForItem(item);
        this.compute$.next(item);
    }

    private setComputeValidationResult({ clik, cliv }: ComputeValidationDataResult) {
        if (!this.get || !cliv) return;

        const itemToUpdate = this.get.checklistitems.find((cli) => cli.checklistitem_keyno === clik);

        if (!!itemToUpdate) {
            itemToUpdate.itemvalue = cliv;
            this.setDependencyVisibilityForItem(itemToUpdate);
        }
    }

    private async computeValidationForItem(item: ChecklistItem): Promise<ComputeValidationResponse | undefined> {
        if (!this.get) throw Error('no get');

        try {
            const keynosSet = new Set(item.cvs.map((c) => c.clik));
            const data: ComputeValidationData[] = this.get.checklistitems.filter((cli) => keynosSet.has(cli.checklistitem_keyno)).map((cli) => ({ cii: cli.checklistitem_keyno, civ: cli.itemvalue }));

            return await this.chechklistData.computeValidation({ item_id: item.checklistitem_keyno, item_value: item.itemvalue, cvd: data });
        } catch (error) {
            console.error(error);
            return;
        }
    }

    private setDependencyVisibilityForItem(item: ChecklistItem) {
        if (!this.get) return;

        if (['CS', 'CB', 'CL'].includes(item.elementtype)) {
            const currentIndex = this.get.checklistitems.findIndex((cli) => cli.checklistitem_keyno === item.checklistitem_keyno);
            const possibleDeps = this.get.checklistitems.slice(currentIndex + 1);
            const deps1 = possibleDeps.filter((i) => i.dep1_checklistitem_keyno === item.checklistitem_keyno);
            const deps2 = possibleDeps.filter((i) => i.dep2_checklistitem_keyno === item.checklistitem_keyno);

            for (let dep of deps1) {
                dep.dep1_visible = dep.dep1_value === item.itemvalue ? '1' : '0';
            }

            for (let dep of deps2) {
                dep.dep2_visible = dep.dep2_value === item.itemvalue ? '1' : '0';
            }
        }
    }

    private async loadSettings() {
        if (!!this.params) {
            this.settings = await this.chechklistData.getChecklistDataSettings(this.params);
            this.settingsLoaded.emit(this.settings);
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.chechklistData.getChecklistData(this.params);

            if (this.get.paged === '1') {
                this.pages = this.getChecklistItemsAsPageStepsMap(this.get.checklistitems);
            }

            this.get.checklistitems.forEach((item) => this.setDependencyVisibilityForItem(item));
        }
    }

    private getChecklistItemsAsPageStepsMap(checklistItems: ChecklistItem[]): Map<ChecklistItem, PageStep> {
        const pages = new Map<ChecklistItem, PageStep>();

        if (checklistItems[0].elementtype !== 'H2') {
            const firstH2Index = checklistItems.findIndex((item) => item.elementtype === 'H2');

            let items: ChecklistItem[] = [];

            if (firstH2Index !== -1) {
                items = checklistItems.slice(0, firstH2Index);
            } else {
                items = checklistItems;
            }

            pages.set(
                checklistItems[0].elementtype === 'H1'
                    ? checklistItems[0]
                    : {
                          // If the schema do not start with a heading a generic start is added
                          checklistitem_keyno: '0',
                          checklistitem_method: 'Start',
                          checklistitemdata_keyno: '0',
                          checklistitemitem: [],
                          cv: '0',
                          cvs: [],
                          dep1_checklistitem_keyno: '',
                          dep1_value: '',
                          dep1_visible: '1',
                          dep2_checklistitem_keyno: '',
                          dep2_value: '',
                          dep2_visible: '1',
                          expected_result: '',
                          itemvalue: '',
                          r: '0',
                          type: '',
                          typevar: '',
                          visible: '1',
                          word_id: '',
                          elementtype: 'H2',
                          page_number: 0,
                      },
                { items: items, complete: false, invalid: false },
            );
        }

        for (let item of checklistItems.filter((i) => i.elementtype === 'H2')) {
            pages.set(item, { items: this.get!.checklistitems.filter((i) => i.page_number === item.page_number && i.elementtype !== 'H2'), complete: false, invalid: false });
        }

        return pages;
    }

    public onPageChange(event: number | `${number}`) {
        for (let [key, value] of this.pages) {
            const index = Array.from(this.pages.keys())
                .filter((page) => page.dep1_visible === '1' && page.dep2_visible === '1')
                .findIndex((page) => page.checklistitem_keyno === key.checklistitem_keyno);

            if (index !== -1 && index <= +event) {
                value.complete = true;
                const requiredItems = value.items.filter((item) => item.r === '1');

                if (requiredItems.length > 0) {
                    if (requiredItems.every((item) => '' + item.itemvalue > '')) {
                        value.invalid = false;
                    } else {
                        value.invalid = true;
                    }
                }
            } else {
                value.complete = false;
            }
        }
    }

    private async init() {
        await Promise.all([this.loadSettings(), this.loadGet()]);
        this.navigator.setHeading?.(this.settings?.page_heading ?? '');
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator?.setSize?.({ width: 'min(95vw, 100rem)' });
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.compute$.complete();
    }

    private async initFromDialog(state: StateDialogContext | undefined) {
        if (!!state && 'checklistdata_keyno' in state.params) {
            state.setSize({ width: 'min(95vw, 100rem)' });
            this.params = state.params as unknown as ChecklistDataParams;
            await this.init();
            state.setHeading(this.settings?.page_heading ?? '');
            this.submit.subscribe({ next: (value: ErrorMessageResponse | undefined) => state.close(value) });
        }
    }
}

interface PageStep {
    /**
     * The items to render in this page step.
     */
    items: ChecklistItem[];

    /**
     * Whether this step has been completed.
     */
    complete: boolean;

    /**
     * Whether this page step is invalid.
     */
    invalid: boolean;
}
